'use client';

import type Layout from './layout';
import { LocalJSONStorage } from '~/utils/js';
import { PARTNER_TYPES } from '@funkey/shared/constants';
import type { RegisterPartnerInput } from '@funkey/types/graphql/graphql';
import { api } from '~/modules';
import { contextFactory } from '~/utils/react';
import { graphql } from '@funkey/types/graphql/gql';
import type { ts } from '@funkey/shared';
import { useEffect } from 'react';
import { useFormState } from '~/modules/form';
import { useI18n } from '~/features/i18n';
import { Step_useCtx } from '~/features/layout/Steps/client';
import { z } from 'zod';
import { I18N_CONF } from '@funkey/locales';

export const [usePartnerRegistrationCtx, PartnerRegistrationCtxProvider] = contextFactory(
    (props: { locale: string; initialType?: (typeof PARTNER_TYPES)[number] }) => {
        const i18n = useI18n();
        const steps = Step_useCtx<(typeof Layout.$STEPS)[number]>();
        const STEPS = steps.state.map(step => step.path);

        type Step = (typeof STEPS)[number];
        type Validators = typeof VALIDATORS;
        type ValidatorKeys = keyof ts.Union.toIntersection<Validators[Step]>;
        type StepValues = { [K in ValidatorKeys]?: string | boolean };
        type DefaultValues = Record<Step, StepValues>;

        const VALIDATORS = getValidators(i18n, props.initialType);

        const defaultValues = STEPS.reduce<DefaultValues>((acc, step) => {
            if (step in VALIDATORS) {
                const stepValues = Object.entries(VALIDATORS[step]).reduce(
                    (stepAcc, [key, config]) => ({
                        ...stepAcc,
                        [key]: config?.default === undefined ? '' : config.default,
                    }),
                    {},
                );

                return {
                    ...acc,
                    [step]: stepValues,
                };
            }
            return acc;
        }, {} as DefaultValues);

        // Persist data between steps
        const FORM_VALUE_STORE = {
            'personal-details': new LocalJSONStorage(
                'PARTNER_REGISTRATION_FORM_VALUES_PERSONAL_DETAILS',
                { ...defaultValues['personal-details'] },
            ),
            'company-details': new LocalJSONStorage(
                'PARTNER_REGISTRATION_FORM_VALUES_COMPANY_DETAILS',
                { ...defaultValues['company-details'] },
            ),
            // TODO: on hold for v1 release. Re-enable this when we have separate terms and conditions for partners
            // 'terms-of-service': new LocalJSONStorage(
            //     'PARTNER_REGISTRATION_FORM_VALUES_TERMS_OF_SERVICE',
            //     { ...defaultValues['terms-of-service'] },
            // ),
        } as const;

        const STEP_PATH = steps.getStepByIdx(steps.currentIdx)?.path || 'personal-details';
        const STEP = steps.getStep(STEP_PATH);
        const STEP_LOCAL_STORE = FORM_VALUE_STORE[STEP_PATH];
        const STEP_VALIDATORS = VALIDATORS[STEP_PATH];

        const storedValues = STEP_LOCAL_STORE.getValue();
        const formState = useFormState<StepValues>(
            props.initialType && STEP_PATH === 'company-details'
                ? { ...storedValues, type: props.initialType }
                : storedValues,
            Object.entries(STEP_VALIDATORS).reduce<
                Record<string, { validate: (value: any) => boolean; message: string }>
            >(
                (acc, [key, { validator, message }]) => ({
                    ...acc,
                    [key]: {
                        validate: (value: string) => validator.safeParse(value).success,
                        message,
                    },
                }),
                {},
            ),
        );

        useEffect(() => {
            STEP.updateState({ isCompleted: true });
            STEP_LOCAL_STORE.setValue(formState.values);
        }, Object.values(formState.values));

        return {
            formState,
            steps,
            getAllStepsValues: (): RegisterPartnerInput =>
                Object.entries(FORM_VALUE_STORE).reduce(
                    (acc, [_, storage]) => ({
                        ...acc,
                        ...(storage.getValue() as RegisterPartnerInput),
                    }),
                    {} as RegisterPartnerInput,
                ),
            isLastStep: steps.currentIdx === steps.state.length - 1,
            registerMutation: api.useGraphqlMutation(MUTATION),
        } as const;
    },
);

function getValidators(
    i18n: ReturnType<typeof useI18n>,
    initialType?: (typeof PARTNER_TYPES)[number],
) {
    return {
        'personal-details': {
            firstName: {
                validator: z.string().min(2),
                message: i18n._('First name is too short'),
            },
            lastName: {
                validator: z.string().min(2),
                message: i18n._('Last name is too short'),
            },
            email: {
                validator: z.email(),
                message: i18n._('Invalid email address'),
            },
            password: {
                validator: z.string().min(6),
                message: i18n._('Password is too short'),
            },
            country: {
                validator: z.enum(I18N_CONF.COUNTRIES),
                message: i18n._('Country is too short'),
                default: i18n.country,
            },
            language: {
                validator: z.enum(I18N_CONF.LANGUAGES),
                message: i18n._('Language is too short'),
                default: i18n.language,
            },
        },
        'company-details': {
            type: {
                validator: z.enum(PARTNER_TYPES),
                message: i18n._('Please select a partner type'),
                ...(initialType ? { default: initialType } : {}),
            },
            companyName: {
                validator: z.string().min(2),
                message: i18n._('Company name is too short'),
            },
            phone: {
                validator: z.string().min(2),
                message: i18n._('Phone is too short'),
            },
            address: {
                validator: z.string().min(2),
                message: i18n._('Address is too short'),
            },
            city: {
                validator: z.string().min(2),
                message: i18n._('City is too short'),
            },
            zipCode: {
                validator: z.string().min(2),
                message: i18n._('Zip code is too short'),
            },
            companyCountry: {
                validator: z.string().min(2),
                message: i18n._('Company country is too short'),
                default: i18n.country,
            },
            vatNumber: {
                validator: z.string().min(2),
                message: i18n._('VAT number is too short'),
            },
            insurranceCompany: {
                validator: z.string().min(2),
                message: i18n._('Insurrance company is too short'),
            },
        },
        // TODO: on hold for v1 release. Re-enable this when we have separate terms and conditions for partners
        // 'terms-of-service': {
        //     tos: {
        //         validator: z.boolean(),
        //         message: i18n._('Please accept the terms of service'),
        //         default: false,
        //     },
        // },
    } as const;
}

const MUTATION = graphql(`
    mutation registrationCreateUser($input: RegisterPartnerInput!) {
        registerPartner(input: $input) {
            id
        }
    }
`);
